/**
 * Closing Agent – Dubai Police 999 Emergency Assistant
 *
 * Wraps up the conversation after any flow (Q&A, agency reporting, handoff).
 * - Asks "Is there anything else I can help you with?"
 * - If YES: routes back to intent_agent (full classification cycle restarts, labels preserved)
 * - If NO: says warm goodbye, calls end_session
 *
 * Incident card display is handled by IncidentCardAgent (not here).
 * Labels are NEVER cleared on transition.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

export const SESSION_ENDED_EVENT = 'dp:sessionEnded';

export function createClosingAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'closing_agent',
    name: 'Closing Agent',
    description: 'Asks "anything else?", routes back to intent agent or ends session gracefully',

    initialPrompt: 'Ask the user warmly if they need anything else (in their language, Khaliji Arabic if Arabic). Route back to intent or end the session based on their response.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_intent', { target: { agentId: 'intent_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'intent_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── route_to_intent ────────────────────────────────────────────────
      if (toolName === 'route_to_intent' || toolName === 'route_to_inquiry') {
        const query = args.query || '';
        console.log('[ClosingAgent] Routing back to intent agent with query:', query);

        return {
          toolName, args,
          result: { success: true, message: 'Routing to intent agent' },
          triggersTransition: true,
          transitionTo: 'intent_agent',
          // Preserve ALL existing agencies — only pass pendingQuestion as a hint
          transitionData: {
            pendingQuestion: query,
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
          },
        };
      }

      // ─── end_session ────────────────────────────────────────────────────
      if (toolName === 'end_session') {
        console.log('[ClosingAgent] Session ended by user');
        window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT));
        return {
          toolName, args,
          result: { success: true, message: 'Session ended gracefully' },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const confirmedAgencies = context.transitionData.confirmedAgencies || [];
      console.log('[ClosingAgent] Activated – confirmed agencies:', confirmedAgencies);
      // Incident card is handled by IncidentCardAgent — no emission here
    },
  };
}

export default createClosingAgent;
